import * as THREE from 'three/webgpu';
import {SketchEntitySymmetricConstraint} from '../solver/constraint/relationDefinition/SketchEntitySymmetricConstraint';
import {detachConstraintFromAllTargets} from '../solver/constraint/utils/detachConstraintFromAllTargets';
import {
    isSketchCollectionObject,
    isSketchSlotObject,
    isSketchMirrorAxisReferenceProvider,
    isSketchObject,
    type ISketchObject,
    type SketchGroup,
} from '../../mesh/sketch';
import type {Viewer} from '../../viewer/Viewer';
import type {
    MirrorAxisGeometryProvider,
    SketchMirrorAxisResolvedGeometry,
    SketchMirrorEntityBaseline,
} from '../../../types/sketch/mirror/SketchMirrorAxisReferenceTypes';
import {AbstractSketchEntitiesTransformController} from './AbstractSketchEntitiesTransformController';
import {SketchReferencedEntityCopyConstraintPolicy} from './SketchReferencedEntityCopyConstraintPolicy';
import {SketchMirrorAxisReferenceResolver} from './SketchMirrorAxisReferenceResolver';
import {
    SketchRelationRetentionService,
    type SketchRelationRetentionContext,
} from './SketchRelationRetentionService';
import {
    SketchEntitiesTransformCommand,
    type SketchEntitiesTransformControllerSnapshot,
    SketchEntitiesTransformPhase,
    type SketchEntitiesTransformRepeatRecord,
    SketchEntitiesTransformSelectionRole,
} from './SketchEntitiesTransformTypes';
import {disposeSketchEntitiesTransformCloneDomainResources} from './utils/disposeSketchEntitiesTransformCloneDomainResources';

/**
 * 二维草图镜向实体控制器。
 *
 * 生命周期边界：控制器只在活动二维草图中工作；预览在取消、清理和释放时全部回滚。
 * Copy 开启时创建副本及对称关系，关闭时原位反射原实体并保留其身份。
 */
export class SketchMirrorEntitiesController extends AbstractSketchEntitiesTransformController {
    /** 当前镜向轴。 */
    private mirrorAxis: MirrorAxisGeometryProvider | null = null;
    /** 承载视口高亮和 PropertyManager 选择项的真实来源对象。 */
    private mirrorAxisSelectionObject: THREE.Object3D | null = null;
    /** 统一解析草图线、模型边、基准面和平面模型面。 */
    private readonly mirrorAxisReferenceResolver: SketchMirrorAxisReferenceResolver;
    /** 是否保留源实体并创建镜向副本。 */
    private copyEnabled = true;
    /** Copy 开启时挂入草图组的临时预览副本。 */
    private previewClones: THREE.Object3D[] = [];
    /** 确认事务内创建的副本；仅用于异常时精确回收本次未提交对象。 */
    private commitClones: THREE.Object3D[] = [];
    /** Copy 关闭时用于无对象替换预览的完整实体定义基线。 */
    private mirrorPreviewBaselines: SketchMirrorEntityBaseline[] | null = null;
    /** Copy 关闭时当前是否已应用镜向预览。 */
    private mirrorPreviewApplied = false;
    /** 基类变换模板在一次 resolve/apply 调用间传递的唯一轴快照。 */
    private resolvedTransformAxisGeometry: SketchMirrorAxisResolvedGeometry | null = null;
    /** 复用复制实体已有的转换引用约束过滤规则。 */
    private readonly referencedEntityConstraintPolicy = new SketchReferencedEntityCopyConstraintPolicy();
    /** 复用移动实体的跨选择域关系断开规则。 */
    private readonly relationRetentionService = new SketchRelationRetentionService();
    /** Copy 关闭时预览阶段可回滚的关系移除上下文。 */
    private relationRetentionPreviewContext: SketchRelationRetentionContext | null = null;

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        super(viewer);
        this.mirrorAxisReferenceResolver = new SketchMirrorAxisReferenceResolver(viewer);
    }

    /**
     * 启动镜向命令。
     *
     * @param initialEntities 命令启动前的预选实体。
     */
    public override start(initialEntities: readonly THREE.Object3D[] = []): void {
        if (!this.resolveActiveSketchGroup()) {
            this.clear();
            return;
        }
        this.rollbackPreview();
        this.releaseDetachedMirrorAxis();
        this.mirrorAxis = null;
        this.mirrorAxisSelectionObject = null;
        this.copyEnabled = true;
        super.start(initialEntities);
    }

    /** 清理命令状态与预览资源。 */
    public override clear(): void {
        this.rollbackPreview();
        this.releaseDetachedMirrorAxis();
        this.mirrorAxis = null;
        this.mirrorAxisSelectionObject = null;
        this.copyEnabled = true;
        this.releaseMirrorPreviewBaselines();
        this.mirrorPreviewApplied = false;
        this.resolvedTransformAxisGeometry = null;
        this.relationRetentionPreviewContext = null;
        this.commitClones = [];
        // 基类会发布清理后的快照，因此镜向专有字段必须先复位。
        super.clear();
    }

    /**
     * 取消命令并释放尚未提交的外部引用。
     *
     * Mirror 的 detached provider 持有运行时 resolver，不能沿用基类只回滚预览但保留选择态的语义；
     * Viewer 随后再次调用 clear 时保持幂等。
     */
    public override cancel(): void {
        this.clear();
    }

    /**
     * 设置是否复制镜向实体。
     *
     * @param enabled true 创建副本，false 原位镜向。
     * @throws 旧预览回滚或新预览创建失败时原样抛出。
     */
    public setCopyEnabled(enabled: boolean): void {
        if (this.copyEnabled === enabled) {
            return;
        }
        this.rollbackPreview();
        this.copyEnabled = enabled;
        this.refreshPhase();
        this.tryPreview();
        this.notifyStateChanged();
    }

    /**
     * 设置镜向命令当前公开选择角色。
     *
     * 镜向命令没有 From/To 或旋转中心输入；`None` 只由控制器在轴选择完成后内部写入，
     * 公共 API 传入其它通用变换角色会令命令永久停在不可交互阶段，因此必须在 cad-core 边界拒绝。
     * @param role 待激活的选择角色。
     * @throws 角色不是实体或镜向轴时抛出明确错误。
     */
    public override setActiveSelectionRole(role: SketchEntitiesTransformSelectionRole): void {
        if (
            role !== SketchEntitiesTransformSelectionRole.Entities
            && role !== SketchEntitiesTransformSelectionRole.MirrorAxis
        ) {
            throw new Error('镜向实体仅支持选择实体或镜向轴。');
        }
        super.setActiveSelectionRole(role);
    }

    /**
     * 按镜向命令的真实实体域消费单击选择。
     *
     * 轮廓集合只是内部拓扑宿主；点击其轮廓时必须一次展开为 collectSet 中的真实边，后续列表、克隆和
     * 对称关系全部沿用同一展开结果，避免预览正确但确认后把关系挂到不可编辑宿主上。
     *
     * @param object 视口命中的草图对象或轮廓代理。
     * @returns 命中至少一个可镜向实体时返回 true。
     */
    public override handleEntityPick(object: THREE.Object3D): boolean {
        const activeSketchGroup = this.resolveActiveSketchGroup();
        if (!activeSketchGroup) {
            return false;
        }
        const entities = this.resolveTransformEntities([object], activeSketchGroup);
        if (entities.length === 0) {
            return false;
        }

        this.rollbackPreview();
        const pickedEntityIds = new Set(entities.map((entity) => entity.uuid));
        const shouldRemove = entities.every((entity) => (
            this.selectedEntities.some((selected) => selected.uuid === entity.uuid)
        ));
        if (shouldRemove) {
            this.selectedEntities = this.selectedEntities.filter(
                (selected) => !pickedEntityIds.has(selected.uuid),
            );
        } else {
            const selectedEntityMap = new Map(
                this.selectedEntities.map((selected) => [selected.uuid, selected]),
            );
            entities.forEach((entity) => selectedEntityMap.set(entity.uuid, entity));
            this.selectedEntities = Array.from(selectedEntityMap.values());
        }
        this.syncSelectionVisuals(this.selectedEntities);
        this.refreshPhase();
        this.tryPreview();
        this.notifyStateChanged();
        return true;
    }

    /**
     * 消费镜向轴选择。
     *
     * @param object 视口命中的对象。
     * @returns 是否成功选中满足二维规则的统一镜像轴来源。
     */
    public handleMirrorAxisPick(object: THREE.Object3D): boolean {
        if (this.activeSelectionRole !== SketchEntitiesTransformSelectionRole.MirrorAxis) {
            return false;
        }
        const sketchGroup = this.resolveActiveSketchGroup();
        const resolution = sketchGroup
            ? this.mirrorAxisReferenceResolver.resolveSelection({object, sketchGroup})
            : null;
        if (!resolution) {
            return false;
        }

        this.rollbackPreview();
        this.releaseDetachedMirrorAxis();
        this.mirrorAxis = resolution.provider;
        this.mirrorAxisSelectionObject = resolution.sourceObject;
        // 同一条直线不能同时承担源实体与镜向轴职责。
        this.selectedEntities = this.selectedEntities.filter((entity) => entity.uuid !== resolution.provider.uuid);
        this.activeSelectionRole = SketchEntitiesTransformSelectionRole.None;
        this.syncSelectionVisuals(this.selectedEntities);
        this.refreshPhase();
        this.tryPreview();
        this.notifyStateChanged();
        return true;
    }

    /** 清除当前镜向轴并恢复轴选择状态。 */
    public clearMirrorAxis(): void {
        this.rollbackPreview();
        this.releaseDetachedMirrorAxis();
        this.mirrorAxis = null;
        this.mirrorAxisSelectionObject = null;
        this.activeSelectionRole = SketchEntitiesTransformSelectionRole.MirrorAxis;
        this.syncSelectionVisuals(this.selectedEntities);
        this.refreshPhase();
        this.notifyStateChanged();
    }

    /**
     * 删除镜向命令中的选择项。
     *
     * @param entityId 实体或镜向轴运行时 ID。
     */
    public override removeSelectedEntity(entityId: string): void {
        if (this.mirrorAxis?.uuid === entityId || this.mirrorAxisSelectionObject?.uuid === entityId) {
            this.clearMirrorAxis();
            return;
        }
        super.removeSelectedEntity(entityId);
    }

    /**
     * 返回包含镜向专有字段的控制器快照。
     *
     * @returns 镜向命令快照。
     */
    public override getSnapshot(): SketchEntitiesTransformControllerSnapshot {
        return {
            ...super.getSnapshot(),
            mirrorAxis: this.mirrorAxisSelectionObject
                ? this.selectionService.buildSelectedItems([this.mirrorAxisSelectionObject])[0] ?? null
                : null,
            copyEnabled: this.copyEnabled,
        };
    }

    /**
     * 确认成功后统一退出镜向命令态。
     *
     * @returns 是否成功提交。
     * @throws 镜向定义写回、关系迁移、历史提交或失败回滚异常时原样抛出。
     */
    public override confirm(): boolean {
        const sketchGroup = this.resolveActiveSketchGroup();
        if (!sketchGroup || !this.hasConfirmSelection()) {
            return false;
        }
        // 外部边、基准面等轴源可能在确认同时被重建；同一事务只解析一次并贯穿全部写回。
        const axisGeometry = this.mirrorAxis?.resolveMirrorAxisGeometry() ?? null;
        if (!axisGeometry) {
            return false;
        }

        this.rollbackPreview();
        let relationRetentionContext: SketchRelationRetentionContext | null = null;
        const externalAxis = isSketchMirrorAxisReferenceProvider(this.mirrorAxis)
            ? this.mirrorAxis
            : null;
        let attachedExternalAxis = false;
        let transactionCommitClones: THREE.Object3D[] = [];
        try {
            this.viewer.executeActiveSketchRuntimeHistoryTransaction({
                label: this.resolveHistoryLabel(),
                commandType: this.resolveHistoryCommandType(),
                work: () => {
                    if (this.copyEnabled && externalAxis && !sketchGroup.hasSketchReferenceTarget(externalAxis)) {
                        // 外部 provider 只在确认事务中成为正式主状态，预览和取消不会污染草图组。
                        sketchGroup.addSketchReferenceTarget(externalAxis);
                        attachedExternalAxis = true;
                    }
                    if (!this.copyEnabled) {
                        relationRetentionContext = this.relationRetentionService.detachReflectionVariantRelations(
                            {sketchGroup, selectedEntities: this.selectedEntities},
                            sketchGroup.referencePlane,
                            axisGeometry,
                        );
                    }
                    this.applyResolvedMirror(axisGeometry, sketchGroup);
                    transactionCommitClones = [...this.commitClones];
                    this.commitSketchGroup(sketchGroup);
                    return true;
                },
                commitExtraState: () => {
                    // provider 等额外资源必须等同步或异步历史提交真正成功后才能最终释放。
                    relationRetentionContext?.commit();
                },
                rollbackExtraState: () => {
                    relationRetentionContext?.rollback();
                    this.rollbackFailedCommit(
                        externalAxis,
                        attachedExternalAxis,
                        sketchGroup,
                        transactionCommitClones,
                    );
                },
            });
            this.commitClones = [];
        } catch (error) {
            relationRetentionContext?.rollback();
            this.rollbackFailedCommit(
                externalAxis,
                attachedExternalAxis,
                sketchGroup,
                transactionCommitClones.length > 0 ? transactionCommitClones : this.commitClones,
            );
            throw error;
        }

        this.phase = SketchEntitiesTransformPhase.Committed;
        this.notifyStateChanged();
        return true;
    }

    /** @returns 镜向命令类型。 */
    protected resolveCommandType(): SketchEntitiesTransformCommand {
        return SketchEntitiesTransformCommand.Mirror;
    }

    /** @returns 当前镜向轴对应的世界坐标反射矩阵。 */
    protected resolveTransformMatrix(): THREE.Matrix4 {
        const sketchGroup = this.resolveActiveSketchGroup();
        if (!sketchGroup || !this.mirrorAxis) {
            throw new Error('镜向草图实体失败：缺少活动草图或镜向轴。');
        }
        const axisGeometry = this.mirrorAxis.resolveMirrorAxisGeometry();
        if (!axisGeometry) {
            throw new Error('镜向草图实体失败：镜向轴来源已悬空或不满足二维草图规则。');
        }
        this.resolvedTransformAxisGeometry = axisGeometry;
        try {
            return this.geometryService.createMirrorMatrix(
                axisGeometry.startWorld,
                axisGeometry.endWorld.clone().sub(axisGeometry.startWorld),
                sketchGroup.referencePlane.normal,
            );
        } catch (error) {
            this.resolvedTransformAxisGeometry = null;
            throw error;
        }
    }

    /**
     * 应用已解析的镜向变换。
     *
     * @param _matrix 已由同一轴快照构造的镜向矩阵；完整定义写回直接消费轴快照。
     * @param sketchGroup 活动草图组。
     */
    protected applyResolvedTransform(_matrix: THREE.Matrix4, sketchGroup: SketchGroup): void {
        const axisGeometry = this.resolvedTransformAxisGeometry;
        this.resolvedTransformAxisGeometry = null;
        if (!axisGeometry) {
            throw new Error('镜向草图实体失败：缺少本次变换已解析的镜向轴。');
        }
        this.applyResolvedMirror(axisGeometry, sketchGroup);
    }

    /**
     * 使用同一份已解析轴几何应用镜像，避免确认事务内重复访问外部引用源。
     * @param axisGeometry 本次事务唯一的世界轴几何。
     * @param sketchGroup 活动草图组。
     */
    private applyResolvedMirror(
        axisGeometry: SketchMirrorAxisResolvedGeometry,
        sketchGroup: SketchGroup,
    ): void {
        if (!this.copyEnabled) {
            this.geometryService.applyMirrorToEntitiesWithoutConstraintSolve(
                this.selectedEntities,
                axisGeometry,
            );
            this.viewer.syncSketchEditingVisualLayering();
            return;
        }

        const sourceObjectCloneMap = new Map<string, THREE.Object3D>();
        const clones = this.cloneMirrorEntities(sketchGroup, axisGeometry, sourceObjectCloneMap);
        // cloneEntities 会先把副本挂入正式组；后续任一步失败都必须能回收完整事务域。
        this.commitClones = clones;
        this.geometryService.applyMirrorToEntitiesWithoutConstraintSolve(
            this.resolveMirroredCloneEntities(sourceObjectCloneMap),
            axisGeometry,
        );
        this.relationRetentionService.migrateClonedReflectionRelations(
            this.selectedEntities,
            sourceObjectCloneMap,
            sketchGroup.referencePlane,
            axisGeometry,
        );
        this.createSymmetryRelations(sourceObjectCloneMap);
        this.viewer.syncSketchEditingVisualLayering();
    }

    /** @returns 镜向历史命令类型。 */
    protected resolveHistoryCommandType(): string {
        return 'sketch.runtime.mirror-entities';
    }

    /** @returns 镜向历史展示名称。 */
    protected resolveHistoryLabel(): string {
        return '镜向草图实体';
    }

    /** @returns 当前实体与镜向轴是否足以确认。 */
    protected canConfirm(): boolean {
        if (
            this.phase === SketchEntitiesTransformPhase.Committed
            || this.phase === SketchEntitiesTransformPhase.Cancelled
        ) {
            return false;
        }
        return this.hasConfirmSelection()
            && this.mirrorAxis?.resolveMirrorAxisGeometry() !== null;
    }

    /**
     * 校验无需访问外部解析器的确认输入。
     * @returns 已选择实体、轴对象存在且轴未混入实体域时返回 true。
     */
    private hasConfirmSelection(): boolean {
        return this.selectedEntities.length > 0
            && this.mirrorAxis !== null
            && !this.selectedEntities.some((entity) => entity.uuid === this.mirrorAxis?.uuid);
    }

    /** 镜向没有 Repeat 语义。 */
    protected canRepeat(): boolean {
        return false;
    }

    /** 镜向没有 Repeat 语义。 */
    protected applyRepeatRecord(_record: SketchEntitiesTransformRepeatRecord, _sketchGroup: SketchGroup): boolean {
        return false;
    }

    /**
     * 镜向轴不得再次进入待镜向实体集合。
     */
    protected override resolveTransformEntity(
        object: THREE.Object3D,
        activeSketchGroup: SketchGroup,
    ): THREE.Object3D | null {
        const entity = super.resolveTransformEntity(object, activeSketchGroup);
        if (entity?.uuid === this.mirrorAxis?.uuid || isSketchCollectionObject(entity)) {
            return null;
        }
        return entity;
    }

    /**
     * 批量选择同样排除当前镜向轴。
     */
    protected override resolveTransformEntities(
        objects: readonly THREE.Object3D[],
        activeSketchGroup: SketchGroup,
    ): THREE.Object3D[] {
        const normalizedEntityMap = new Map<string, THREE.Object3D>();
        const resolvedEntities = this.selectionService.resolveTransformEntities(
            objects,
            activeSketchGroup,
            {promoteSlotComponentsToOwner: true},
        );
        const selectedSlotComponentIds = new Set<string>();
        resolvedEntities.forEach((entity) => {
            if (isSketchSlotObject(entity)) {
                entity.getEdges().forEach((edge) => selectedSlotComponentIds.add(edge.uuid));
                entity.getJunctionPointArray().forEach((point) => selectedSlotComponentIds.add(point.uuid));
            }
        });
        resolvedEntities.forEach((entity) => {
            if (entity.uuid === this.mirrorAxis?.uuid) {
                return;
            }
            if (selectedSlotComponentIds.has(entity.uuid)) {
                return;
            }
            if (isSketchCollectionObject(entity)) {
                entity.collectSet.forEach((source) => {
                    if (source.uuid !== this.mirrorAxis?.uuid) {
                        normalizedEntityMap.set(source.uuid, source);
                    }
                });
                return;
            }
            normalizedEntityMap.set(entity.uuid, entity);
        });
        return Array.from(normalizedEntityMap.values());
    }

    /** 按 Copy 模式刷新命令阶段。 */
    protected override refreshPhase(): void {
        if (!this.active) {
            this.phase = SketchEntitiesTransformPhase.Cancelled;
            return;
        }
        if (this.canConfirm()) {
            this.phase = (this.copyEnabled ? this.previewClones.length > 0 : this.mirrorPreviewApplied)
                ? SketchEntitiesTransformPhase.Previewing
                : SketchEntitiesTransformPhase.Ready;
            return;
        }
        this.phase = this.activeSelectionRole === SketchEntitiesTransformSelectionRole.Entities
            ? SketchEntitiesTransformPhase.SelectEntities
            : SketchEntitiesTransformPhase.SelectReference;
    }

    /** 刷新镜向预览。 */
    protected override tryPreview(): void {
        const sketchGroup = this.resolveActiveSketchGroup();
        const axisGeometry = this.active && sketchGroup && this.hasConfirmSelection()
            ? this.mirrorAxis?.resolveMirrorAxisGeometry() ?? null
            : null;
        if (!sketchGroup || !axisGeometry) {
            this.rollbackPreview();
            return;
        }
        if (!this.copyEnabled) {
            this.rollbackPreview();
            try {
                this.ensureMirrorPreviewBaseline();
                this.mirrorPreviewApplied = true;
                this.relationRetentionPreviewContext = this.relationRetentionService.detachReflectionVariantRelations(
                    {sketchGroup, selectedEntities: this.selectedEntities},
                    sketchGroup.referencePlane,
                    axisGeometry,
                );
                this.applyResolvedMirror(axisGeometry, sketchGroup);
                this.viewer.requestRender();
                this.refreshPhase();
            } catch (error) {
                this.rollbackPreview();
                throw error;
            }
            return;
        }

        this.rollbackPreview();
        try {
            const sourceObjectCloneMap = new Map<string, THREE.Object3D>();
            this.previewClones = this.cloneMirrorEntities(sketchGroup, axisGeometry, sourceObjectCloneMap);
            this.geometryService.applyMirrorToEntitiesWithoutConstraintSolve(
                this.resolveMirroredCloneEntities(sourceObjectCloneMap),
                axisGeometry,
            );
            this.relationRetentionService.migrateClonedReflectionRelations(
                this.selectedEntities,
                sourceObjectCloneMap,
                sketchGroup.referencePlane,
                axisGeometry,
            );
            this.viewer.syncSketchEditingVisualLayering();
            this.viewer.requestRender();
            this.refreshPhase();
        } catch (error) {
            this.rollbackPreview();
            throw error;
        }
    }

    /** 回滚临时副本或原位镜向预览。 */
    protected override rollbackPreview(): void {
        if (!this.copyEnabled) {
            try {
                if (this.mirrorPreviewApplied) {
                    this.restoreMirrorPreviewBaseline();
                    this.viewer.requestRender();
                }
            } finally {
                this.mirrorPreviewApplied = false;
                try {
                    this.releaseMirrorPreviewBaselines();
                } finally {
                    try {
                        this.relationRetentionPreviewContext?.rollback();
                    } finally {
                        this.relationRetentionPreviewContext = null;
                    }
                }
            }
            return;
        }
        if (this.previewClones.length === 0) {
            return;
        }
        this.previewClones.forEach((clone) => clone.parent?.remove(clone));
        disposeSketchEntitiesTransformCloneDomainResources(this.previewClones);
        this.previewClones = [];
        this.viewer.syncSketchEditingVisualLayering();
        this.viewer.requestRender();
    }

    /**
     * 同步源实体与镜向轴的选择视觉。
     *
     * @param selectedEntities 当前源实体。
     */
    protected override syncSelectionVisuals(selectedEntities: readonly THREE.Object3D[]): void {
        super.syncSelectionVisuals(
            this.mirrorAxisSelectionObject
                ? [...selectedEntities, this.mirrorAxisSelectionObject]
                : selectedEntities,
        );
    }

    /**
     * 按复制实体既有规则创建镜向副本。
     *
     * @param sketchGroup 活动草图组。
     * @param axisGeometry 本次镜像轴的唯一几何快照。
     * @param sourceObjectCloneMap 可选的真实源对象到副本映射。
     * @returns 已挂入草图组的副本域。
     */
    private cloneMirrorEntities(
        sketchGroup: SketchGroup,
        axisGeometry: SketchMirrorAxisResolvedGeometry,
        sourceObjectCloneMap?: Map<string, THREE.Object3D>,
    ): THREE.Object3D[] {
        const referencedEntityPredicate = this.referencedEntityConstraintPolicy
            .createClonePredicate(this.selectedEntities);
        const reflectionClonePredicate = this.relationRetentionService.createReflectionClonePredicate(
            sketchGroup.referencePlane,
            axisGeometry,
            referencedEntityPredicate,
        );
        return this.geometryService.cloneEntities(
            this.selectedEntities,
            sketchGroup,
            undefined,
            undefined,
            reflectionClonePredicate,
            sourceObjectCloneMap,
        );
    }

    /**
     * 从复制服务的完整挂载域中恢复用户选择对应的真实镜像实体。
     *
     * 闭合边集合会被复制服务重建为一个集合根对象，但完整定义镜像和实体关系仍应作用于集合内部的
     * 真实边；因此不能直接把“用于场景生命周期的挂载根”误当作几何变换目标。
     *
     * @param sourceObjectCloneMap 真实源对象到最终副本对象的映射。
     * @returns 与用户选择顺序一致的真实副本实体。
     * @throws 任一源实体缺少最终副本映射时抛出错误。
     */
    private resolveMirroredCloneEntities(
        sourceObjectCloneMap: ReadonlyMap<string, THREE.Object3D>,
    ): THREE.Object3D[] {
        return this.selectedEntities.map((sourceEntity) => {
            const mirroredEntity = sourceObjectCloneMap.get(sourceEntity.uuid);
            if (!mirroredEntity) {
                throw new Error(`镜像草图实体失败：源实体缺少最终副本映射（uuid=${sourceEntity.uuid}）。`);
            }
            return mirroredEntity;
        });
    }

    /**
     * 为每个顶层源实体与其副本建立一条实体级双向对称关系。
     *
     * @param sourceObjectCloneMap cloneEntities 产出的源对象到副本映射。
     */
    private createSymmetryRelations(sourceObjectCloneMap: ReadonlyMap<string, THREE.Object3D>): void {
        const mirrorAxis = this.mirrorAxis;
        if (!mirrorAxis) {
            return;
        }
        this.selectedEntities.forEach((sourceEntity) => {
            const mirroredEntity = sourceObjectCloneMap.get(sourceEntity.uuid);
            if (!isSketchObject(sourceEntity) || !isSketchObject(mirroredEntity)) {
                return;
            }
            const relation = new SketchEntitySymmetricConstraint(mirrorAxis, sourceEntity, mirroredEntity);
            mirrorAxis.constraints.addRelation(relation);
            sourceEntity.constraints.addRelation(relation);
            mirroredEntity.constraints.addRelation(relation);
        });
    }

    /**
     * 释放尚未进入正式草图主状态的外部镜像轴 provider。
     * 已确认并挂入 SketchGroup 的 provider 由组删除与关系删除生命周期接管。
     */
    private releaseDetachedMirrorAxis(): void {
        if (!isSketchMirrorAxisReferenceProvider(this.mirrorAxis)) {
            return;
        }
        const sketchGroup = this.resolveActiveSketchGroup();
        if (!sketchGroup?.hasSketchReferenceTarget(this.mirrorAxis)) {
            this.mirrorAxis.dispose();
        }
    }

    /**
     * 回收确认事务未提交的副本、三端关系与外部引用。
     *
     * 真正 Viewer 会先通过历史快照恢复对象树，轻量测试宿主则可能不会；本方法同时兼容两种边界：
     * 关系始终按逻辑约束全目标解绑，渲染资源只在副本仍挂于场景时由本控制器负责释放。
     *
     * @param externalAxis 本次选择的外部轴 provider。
     * @param attachedExternalAxis provider 是否曾在事务中挂入草图组。
     * @param sketchGroup 本次事务所属草图组。
     * @param commitClones 本次事务创建的副本域。
     */
    private rollbackFailedCommit(
        externalAxis: MirrorAxisGeometryProvider | null,
        attachedExternalAxis: boolean,
        sketchGroup: SketchGroup,
        commitClones: readonly THREE.Object3D[],
    ): void {
        const failedConstraints = new Set<Parameters<typeof detachConstraintFromAllTargets>[0]>();
        commitClones.forEach((clone) => {
            clone.traverse((object) => {
                if (isSketchObject(object)) {
                    object.constraints.getRelations().forEach((relation) => {
                        failedConstraints.add(relation);
                    });
                }
            });
        });
        failedConstraints.forEach(detachConstraintFromAllTargets);

        const attachedFailedClones = commitClones.filter((clone) => clone.parent !== null);
        attachedFailedClones.forEach((clone) => clone.parent?.remove(clone));
        disposeSketchEntitiesTransformCloneDomainResources(attachedFailedClones);
        this.commitClones = [];

        if (isSketchMirrorAxisReferenceProvider(externalAxis)) {
            const ownsExternalAxis = this.mirrorAxis === externalAxis;
            if (attachedExternalAxis && sketchGroup.hasSketchReferenceTarget(externalAxis)) {
                sketchGroup.removeSketchReferenceTarget(externalAxis);
            }
            externalAxis.dispose();
            // 异步历史失败可能晚于 UI 清理；只回收本事务 provider，不能重新激活已退出的命令状态。
            if (!ownsExternalAxis) {
                return;
            }
            this.mirrorAxis = null;
            this.mirrorAxisSelectionObject = null;
            if (!this.active) {
                return;
            }
            this.activeSelectionRole = SketchEntitiesTransformSelectionRole.MirrorAxis;
            this.syncSelectionVisuals(this.selectedEntities);
            this.refreshPhase();
            this.notifyStateChanged();
        }
    }

    /** 捕获 Copy 关闭预览所需的源点基线。 */
    private ensureMirrorPreviewBaseline(): void {
        if (this.mirrorPreviewBaselines) {
            return;
        }
        this.mirrorPreviewBaselines = this.geometryService.captureMirrorEntityBaselines(this.selectedEntities);
    }

    /** 从基线恢复 Copy 关闭预览，避免用历史快照替换对象树。 */
    private restoreMirrorPreviewBaseline(): void {
        if (!this.mirrorPreviewBaselines) {
            return;
        }
        this.geometryService.restoreMirrorEntityBaselines(
            this.mirrorPreviewBaselines,
            this.selectedEntities,
        );
    }

    /** 释放 Copy 关闭预览持有的完整定义快照。 */
    private releaseMirrorPreviewBaselines(): void {
        if (!this.mirrorPreviewBaselines) {
            return;
        }
        this.geometryService.disposeMirrorEntityBaselines(this.mirrorPreviewBaselines);
        this.mirrorPreviewBaselines = null;
    }

}
